/**
 * @file Permission 模块常量定义
 * @description 定义权限管理相关的常量映射和配置
 * 
 * @module @bayes-mes/shared/types/permission
 * @category Types - Permission
 * 
 * @backend-files
 * - backend/app/Models/Permission.php (getTypeTextAttribute, getStatusTextAttribute)
 * - backend/app/Http/Controllers/Api/V1/PermissionController.php (getActionLabel)
 * 
 * @notes
 * - 常量名使用 UPPER_SNAKE_CASE
 * - 提供枚举值到中文标签的映射
 * - 用于前端下拉选择、状态展示等场景
 * 
 * @update-trigger
 * - 枚举类型变更时
 * - Model 静态属性或方法变更时
 * 
 * @validation
 * - 运行验证：对照 Model 的 getTypeTextAttribute 等方法
 * - 确认标签文本与后端一致
 * 
 * @last-updated 2025-10-18
 * @author YSFL Team
 */

import { PermissionType, PermissionStatus } from './enums'

/**
 * 权限类型标签映射
 */
export const PERMISSION_TYPE_LABELS: Record<PermissionType, string> = {
  [PermissionType.MENU]: '菜单权限',
  [PermissionType.BUTTON]: '按钮权限',
  [PermissionType.API]: 'API权限',
  [PermissionType.DATA]: '数据权限',
}

/**
 * 权限状态标签映射
 */
export const PERMISSION_STATUS_LABELS: Record<PermissionStatus, string> = {
  [PermissionStatus.DISABLED]: '禁用',
  [PermissionStatus.ENABLED]: '启用',
}

/**
 * 操作动作标签映射
 * @description 常见操作的中文标签
 */
export const PERMISSION_ACTION_LABELS: Record<string, string> = {
  // 基础 CRUD
  view: '查看',
  create: '创建',
  update: '更新',
  delete: '删除',
  // 导入导出
  export: '导出',
  import: '导入',
  // 其他常见操作
  assign: '分配',
  reset: '重置',
  approve: '审批',
  reject: '驳回',
  enable: '启用',
  disable: '禁用',
  lock: '锁定',
  unlock: '解锁',
}

/**
 * 模块标签映射
 * @description 系统模块的中文标签
 */
export const PERMISSION_MODULE_LABELS: Record<string, string> = {
  system: '系统管理',
  user: '用户管理',
  production: '生产管理',
  warehouse: '仓库管理',
  quality: '质量管理',
  other: '其他',
}

/**
 * 资源标签映射
 * @description 常见资源的中文标签
 */
export const PERMISSION_RESOURCE_LABELS: Record<string, string> = {
  // 单数形式
  user: '用户',
  role: '角色',
  menu: '菜单',
  permission: '权限',
  // 复数形式
  users: '用户',
  roles: '角色',
  menus: '菜单',
  permissions: '权限',
  // 其他
  default: '默认',
}

/**
 * HTTP 方法标签映射
 */
export const HTTP_METHOD_LABELS: Record<string, string> = {
  GET: '查询',
  POST: '创建',
  PUT: '更新',
  PATCH: '修改',
  DELETE: '删除',
}

/**
 * 默认 CRUD 操作列表
 */
export const DEFAULT_CRUD_ACTIONS = ['view', 'create', 'update', 'delete'] as const

/**
 * 扩展操作列表（包含导入导出）
 */
export const EXTENDED_ACTIONS = [
  'view',
  'create',
  'update',
  'delete',
  'export',
  'import',
] as const

